<template>
  <div :id="`${name}-wrapper`" class="secure-pass-field">
    <span :id="`${name}-placeholder`" class="secure-placeholder">{{ placeholder }}</span>
    <input :id="`${name}-value`" class="default input_search" type="password" disabled>
    <div :id="`${name}KeyboardTrigger`" class="ipt_pwd_rjp default" />
  </div>
</template>

<script setup lang="ts">
import { type ISecurePassowrdInputOption, type ISecurePassword } from '../../index';
import { loadScript, loadStyle } from '@core/tools';
import { nextTick, ref, watch, onBeforeUnmount } from 'vue';

declare const showKeyBord: any;

defineOptions({ name: 'SecurePasswordInput' });

defineExpose({ clearPassword });

const emit = defineEmits<{change: [value: ISecurePassword]}>();

const props = defineProps({
  /**
     * 加密输入框的随机密码因子
     * @see https://open.lianlianpay.com/docs/accp/accpstandard/pch5-passguard-guide.html
     * ```tsx
     * // 数据结构如下：
     * const res = {
     *    license: '',
     *    mapArr: '',
     *    oid_partner: '',
     *    randomKey: '',
     *    randomValue: '',
     *    ret_code: '0',
     *    ret_msg: '成功',
     *    rsaPublicContent: '',
     *    sm2KeyHex: '',
     *    user_id: '',
     * };
     * ```
    */
  randomKeys: {
    type: Object,
    required: true,
    default() {
      return {};
    },
  },
  name: {
    type: String,
    required: true,
    default: '',
  },
  placeholder: {
    type: String,
    default: '请输入密码',
  },
  minLength: {
    type: Number,
    default: 1,
  },
  maxLength: {
    type: Number,
    default: 80,
  },
  modalTitle: {
    type: String,
    default: '安全支付输入',
  },
});

const passwordInstance = ref<any>(null);

function initPassword(options = {} as ISecurePassowrdInputOption) {
  passwordInstance.value = new showKeyBord({
    png: '//mpay-static.oss-cn-hangzhou.aliyuncs.com/html/lib/state-secret/img', // 图片路径，用这个就行了，也可以下载到本地
    // 密码校验成功回调
    callBack: () => {
      console.log('密码加密成功');
      passwordInstance.value.setRandKey(options.randomValue); // 先设置随机key
      const normalPass = passwordInstance.value.getOutputSM(); // 国密加密密码
      // const rsaPass = passwordInstance.value.getOutput() // RSA加密密码

      // hash用于比对密码是否一致，同一个密码加密密文可能不同，hash一定相同
      const hash = passwordInstance.value.getHash(); // RSA加密密码

      console.log(`${props.name}加密密码是`, normalPass);
      console.log(`${props.name}密码HASH是`, hash);

      emit('change', {
        password: normalPass,
        hash,
        length: passwordInstance.value.valueStr,
      });
    },
    // 密码校验失败回调
    errorCallBack: () => {
      console.log('密码加密失败');
      emit('change', {
        password: '',
        hash: '',
        length: 0,
      });
    },
    titlefont: props.modalTitle,
    click_objId: `#${props.name}KeyboardTrigger`, // 触发显示键盘的Dom节点Id
    Input_texId: `#${props.name}-value`, // 接收内容的Dom节点Id
    parent_objId: `#${props.name}-wrapper`, // 将键盘插入到何处的Dom节点Id
    placeholderId: `#${props.name}-placeholder`, // span标签的id，该span用于模拟Input_texId的placeholder
    len: String(props.maxLength), // 表示最大允许输入多少个字符[可以不传递]
    regExp1: '[\\S\\s]', // 输入过程限制的正则，不匹配正则的字符不让输入
    regExp2: `^[\\S\\s]{${props.minLength},${props.maxLength}}$`, // 输入完成后的正则，与getValid()对应，判断输入值是否匹配该正则
    keyBordLeft: '',
    keyBordTop: '',
    chaosMode: 0, // 混乱模式,0:无混乱;1:打开时乱一次;2:每输入一个字符乱一次,默认值0
    clickStatus: 1, // 按键状态,0:鼠标划过、悬浮时按键无变化;1:鼠标划过、悬浮时按键的颜色变化,默认值0
    backfont: '退 格',
    capsfont: '切换大/小写',
    surefont: '确定',
    isen: false,
    rsaPublicKey: options.rsaPublicContent, // RSA加密必须参数，国密加密可不要
    sm2KeyHex: options.sm2KeyHex, // 国密加密必须参数，RSA加密可不要
    license: options.license, // 必须参数
  });

  passwordInstance.value.initOptions();
}

function clearPassword() {
  if (passwordInstance.value && passwordInstance.value.clearPWD) {
    passwordInstance.value.clearPWD();

    if (document.getElementById(`${name}-placeholder`)) {
      (document.getElementById(`${name}-placeholder`) as HTMLInputElement).style.display = 'block';
    }
  }
}

async function loadScripts(onSuccess?: () => void) {
  try {
    await loadScript(
      '/jquery.min.js',
      'app-jquery',
    );

    await loadStyle(
      'https://mpay-static.oss-cn-hangzhou.aliyuncs.com/html/lib/state-secret/microdone.css',
      'secure-passstyle',
    );

    setTimeout(async () => {
      await loadScript(
        'https://mpay-static.oss-cn-hangzhou.aliyuncs.com/html/lib/state-secret/microdone-220322.js',
        'secure-password',
      );

      onSuccess && onSuccess();
    });
  } catch (error) {
    //
  }
}

watch(
  () => props.randomKeys,
  (newdata) => {
    if (Object.keys(newdata).length > 0 && newdata.sm2KeyHex) {
      console.log('初始化密码输入框', newdata);

      loadScripts(() => {
        nextTick(() => {
          setTimeout(() => {
            initPassword(newdata);
          }, 300);
        });
      });
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearPassword();
});

loadScripts();
</script>

<style lang="scss" scoped>
.secure-pass-field {
  height: 40px;
  position: relative;
  border: 1px solid var(--color-border-base);
  border-radius: 2px;

  .input_search {
    padding-left: 14px;
  }
}

.secure-placeholder {
  color: #c0c4cc;
  padding-left: 14px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
}
</style>
